import React, { useEffect, useMemo, useRef } from 'react';
import { Form } from 'react-bootstrap';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { toUpper } from 'lodash-es';

const FALLBACK_TOKEN =
  'pk.eyJ1IjoicmVudGFsbWIiLCJhIjoiY21qMDZpMXJkMDV0NDNkc2ZlZXA3ejNjbCJ9.8EJGIUXFLI5w2tSQgmcPXQ';

const AddressForm = ({
  formik,
  basePath = 'shop',
  errorInfo,
  token,
  language = 'en',
  countries = '',
  placeholder = 'Enter your address',
}) => {
  const geocoderContainerRef = useRef(null);
  const geocoderRef = useRef(null);
  const inputElRef = useRef(null);

  const suppressInvalidateRef = useRef(false);
  const isFocusedRef = useRef(false);

  // ✅ 用来判断“是否真的改了文本”
  const initialTextRef = useRef('');

  const values = formik?.values?.[basePath] || {};

  const presetText = useMemo(() => {
    const d = values || {};
    const parts = [];
    if (d.address) parts.push(String(d.address));
    if (d.postalCode || d.postCode) parts.push(String(d.postalCode ?? d.postCode));
    if (d.city) parts.push(String(d.city));
    if (d.state) parts.push(String(d.state));
    if (d.country) parts.push(String(d.country));
    if (d.isoCode) parts.push(String(d.isoCode));
    return parts.join(', ');
  }, [
    values.address,
    values.postCode,
    values.postalCode,
    values.city,
    values.state,
    values.country,
    values.isoCode,
  ]);

  // ✅ 地址被手动编辑时：同时作废 placeId + locationId
  const invalidateSelectionOnce = () => {
    const v = formik?.values?.[basePath] || {};
    if (!v.placeId && !v.locationId) return;

    formik.setValues(
      (prev) => ({
        ...prev,
        [basePath]: {
          ...(prev?.[basePath]),
          placeId: null,
          locationId: null,
        },
      }),
      false
    );
  };

  const fillFromResult = (r) => {
    if (!r) return;

    const fullText = (r.address ? `${r.text}, ${r.address}` : r.text).trim();

    const next = {
      placeId: r.id,
      locationId: null, // ✅ 选了新地址：旧 locationId 作废，等待后端生成/匹配新的
      address: fullText,

      // ✅ 拆开：country 用英文名，isoCode 用 short_code
      country: '',
      isoCode: '',

      state: '',
      city: '',
      postCode: '',
      latitude: r.center?.[1] ?? null,
      longitude: r.center?.[0] ?? null,
    };

    (r.context || []).forEach((c) => {
      if (c.id.startsWith('country.')) {
        next.isoCode = toUpper(c.short_code);
        next.country = c.text_en || c.text;
      } else if (c.id.startsWith('region.')) next.state = c.text;
      else if (c.id.startsWith('district.') || c.id.startsWith('place.')) next.city = c.text;
      else if (c.id.startsWith('postcode.')) next.postCode = c.text;
    });

    suppressInvalidateRef.current = true;
    formik.setValues(
      (prev) => ({
        ...prev,
        [basePath]: { ...(prev?.[basePath]), ...next },
      }),
      false
    );

    // ✅ 选中下拉结果后，把它作为新的“未修改基准”
    initialTextRef.current = fullText;

    setTimeout(() => {
      suppressInvalidateRef.current = false;
    }, 0);
  };

  useEffect(() => {
    if (geocoderRef.current || !geocoderContainerRef.current) return;

    const geocoder = new MapboxGeocoder({
      accessToken: token || FALLBACK_TOKEN,
      types: 'address',
      placeholder,
      countries: countries || undefined,
      language,
      debounce: 300,
      marker: false,
    });

    geocoder.addTo(geocoderContainerRef.current);
    geocoderRef.current = geocoder;

    const input = geocoderContainerRef.current.querySelector(
      'input.mapboxgl-ctrl-geocoder--input'
    );
    inputElRef.current = input || null;

    const onFocus = () => {
      isFocusedRef.current = true;
    };
    const onBlur = () => {
      isFocusedRef.current = false;
    };

    // ✅ 只有当“输入内容 != initialTextRef” 才作废（否则允许编辑页原值直接保存）
    const onInput = () => {
      if (suppressInvalidateRef.current) return;

      const currentText = (inputElRef.current?.value || '').trim();
      const initialText = (initialTextRef.current || '').trim();

      // 没有基准时（极少数情况）直接作废
      if (!initialText) {
        invalidateSelectionOnce();
        return;
      }

      // ✅ 文本没变：不作废
      if (currentText === initialText) return;

      // ✅ 文本变了：作废 placeId/locationId，要求重新从下拉选择
      invalidateSelectionOnce();
    };

    if (input) {
      input.addEventListener('focus', onFocus);
      input.addEventListener('blur', onBlur);
      input.addEventListener('input', onInput);
    }

    // 初始回填（编辑页）
    if (presetText) {
      suppressInvalidateRef.current = true;
      geocoder.setInput(presetText);

      // ✅ 关键：把初始回填文本作为“未修改基准”
      initialTextRef.current = presetText.trim();

      setTimeout(() => {
        suppressInvalidateRef.current = false;
      }, 0);
    }

    geocoder.on('result', (e) => {
      fillFromResult(e?.result);
    });

    geocoder.on('clear', () => {
      suppressInvalidateRef.current = true;
      formik.setValues(
        (prev) => ({
          ...prev,
          [basePath]: {
            ...(prev?.[basePath]),
            placeId: null,
            locationId: null,
            address: '',
            country: '',
            isoCode: '',
            state: '',
            city: '',
            postCode: '',
            latitude: null,
            longitude: null,
          },
        }),
        false
      );

      // 清空后基准也清掉
      initialTextRef.current = '';

      setTimeout(() => {
        suppressInvalidateRef.current = false;
      }, 0);
    });

    return () => {
      if (input) {
        input.removeEventListener('focus', onFocus);
        input.removeEventListener('blur', onBlur);
        input.removeEventListener('input', onInput);
      }
      geocoder.onRemove();
      geocoderRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik, basePath, token, language, countries, placeholder]);

  // ✅ 非聚焦状态下：如果外部 values 变化了，回显到输入框，同时刷新“未修改基准”
  useEffect(() => {
    if (!geocoderRef.current) return;
    if (!presetText) return;
    if (isFocusedRef.current) return;

    const input = inputElRef.current;
    const current = (input?.value || '').trim();
    const next = presetText.trim();
    if (current === next) return;

    suppressInvalidateRef.current = true;
    geocoderRef.current.setInput(next);

    // ✅ 同步基准（避免回显后立刻被当成“修改”）
    initialTextRef.current = next;

    setTimeout(() => {
      suppressInvalidateRef.current = false;
    }, 0);
  }, [presetText]);

  return (
    <Form.Group className="mb-3">
      <Form.Label className="fw-5">
        Fiscal Address Line 1 <span className="text-danger">*</span>
      </Form.Label>

      <div ref={geocoderContainerRef} className="mapbox-geocoder-container w-100" />

      <Form.Text className="text-muted mt-1">Select an address from the dropdown to validate.</Form.Text>

      <Form.Control.Feedback type="invalid">{errorInfo}</Form.Control.Feedback>
    </Form.Group>
  );
};

export default AddressForm;
